import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, computed, inject, Injectable, Provider, Signal, signal } from '@angular/core';
import { Observable, of, tap } from 'rxjs';

/**
 * @description Provider that initializes the `FeatureFlagService` during app
 * initialization
 * @param {string | Record<string, boolean>} urlOrFlags either a `string` that serves
 * as a URL to fetch the feature flags, or a record that represents a set of feature
 * flags.
 * @returns {Provider} The actual provider
 * @example use with AppModule:
 *
 * ```typescript
 * const features = {
 *     featureA: true,
 *     featureB: false,
 * };
 *
 * @NgModule({
 *     providers: [provideFeatureFlags(features)],
 * })
 * export class AppModule {}
 * ```
 */
export const provideFeatureFlags = (urlOrFlags: string | Record<string, boolean>): Provider => ({
    provide: APP_INITIALIZER,
    useFactory: (): (() => Observable<Record<string, boolean>>) => {
        const featureFlagService = inject(FeatureFlagsService);
        return () => featureFlagService.initFeatureFlags(urlOrFlags);
    },
    deps: [],
    multi: true,
});

/**
 * @description Service that provides access to feature flags
 * for components and other services.
 */
@Injectable({
    providedIn: 'root',
})
export class FeatureFlagsService {
    private http = inject(HttpClient);
    private _features = signal<Record<string, boolean>>({});

    public features = computed(() => this._features());

    public hasFeature(flag: string): boolean;
    public hasFeature(flag: string, asSignal: false): boolean;
    public hasFeature(flag: string, asSignal: true): Signal<boolean>;
    public hasFeature(flag: string, asSignal?: boolean): boolean | Signal<boolean> {
        if (asSignal === true) {
            return computed(() => this._features()[flag] ?? false);
        }
        return this._features()[flag] ?? false;
    }

    public initFeatureFlags(url: string): Observable<Record<string, boolean>>;
    public initFeatureFlags(flags: Record<string, boolean>): Observable<Record<string, boolean>>;
    public initFeatureFlags(urlOrFlags: string | Record<string, boolean>): Observable<Record<string, boolean>>;
    public initFeatureFlags(urlOrFlags: string | Record<string, boolean>): Observable<Record<string, boolean>> {
        if (typeof urlOrFlags === 'string') {
            return this.http
                .get<Record<string, boolean>>(urlOrFlags)
                .pipe(tap((features: Record<string, boolean>) => this._features.set(features)));
        } else {
            this._features.set(urlOrFlags);
            return of(urlOrFlags);
        }
    }
}
